import { useMemo } from 'react'

const HIGHLIGHTED_COLOR = '#35A8FD'
const HIGHLIGHTED_LIGHT = '#EBF6FF'
const LIGHT_GREY = '#F3F4F6'

interface UsePieChartDataProps {
  segmentOne: number
  segmentTwo: number
  segmentThree: number
  segmentFour?: number
  highlightedSegment: number
  showAdditionalOverlays?: boolean
  highlightAllSegments?: boolean
}

export interface SegmentData {
  value: number
  originalValue?: number
  itemStyle: {
    color: string
    borderColor: string
    borderWidth: number
  }
}

interface CreateSegment {
  createSegment: (_segment: number, _isInner: boolean) => SegmentData
}

export const usePieChartData = ({
  segmentOne,
  segmentTwo,
  segmentThree,
  segmentFour,
  highlightedSegment,
  showAdditionalOverlays = false,
  highlightAllSegments = false,
}: UsePieChartDataProps): CreateSegment => {
  const ifEmptySegments =
    segmentOne === 0 && segmentTwo === 0 && segmentThree === 0 && (segmentFour === undefined || segmentFour === 0)
  const data: SegmentData[] = useMemo(() => {
    const createSegmentWithMinSize = (value: number, segmentNumber: number): SegmentData => {
      const isSmallValue = value > 0 && value < 2
      const visualValue = isSmallValue ? 2 : value

      return {
        value: visualValue,
        originalValue: isSmallValue ? value : undefined,
        itemStyle: {
          color: highlightedSegment === segmentNumber ? HIGHLIGHTED_COLOR : LIGHT_GREY,
          borderColor: '#fff',
          borderWidth: 2,
        },
      }
    }

    const extraSegments = segmentFour ? [createSegmentWithMinSize(segmentFour, 4)] : []

    return [
      createSegmentWithMinSize(segmentOne, 1),
      createSegmentWithMinSize(segmentTwo, 2),
      createSegmentWithMinSize(segmentThree, 3),
      ...extraSegments,
    ]
  }, [segmentOne, segmentTwo, segmentThree, segmentFour, highlightedSegment])

  const segmentColorMap: Record<number, Record<number, string>> = {
    1: { 0: HIGHLIGHTED_COLOR, 1: LIGHT_GREY, 2: LIGHT_GREY, 3: LIGHT_GREY },
    2: { 0: LIGHT_GREY, 1: HIGHLIGHTED_COLOR, 2: LIGHT_GREY, 3: LIGHT_GREY },
    3: { 0: LIGHT_GREY, 1: LIGHT_GREY, 2: HIGHLIGHTED_COLOR, 3: LIGHT_GREY },
    4: { 0: LIGHT_GREY, 1: LIGHT_GREY, 2: LIGHT_GREY, 3: HIGHLIGHTED_COLOR },
  }

  const getSegmentColor = (segment: number, isInner: boolean): string => {
    if (highlightAllSegments) {
      return isInner ? HIGHLIGHTED_LIGHT : HIGHLIGHTED_COLOR
    }

    if (isInner && !showAdditionalOverlays && highlightedSegment !== segment + 1) {
      return '#F9FAF9'
    }

    const color = segmentColorMap[highlightedSegment][segment]
    return isInner ? HIGHLIGHTED_LIGHT : color
  }

  const getEmptySegmentColor = (isInner: boolean): string => {
    return isInner ? `#F4F4F4` : '#EDEEED'
  }

  const createSegment = (segment: number, isInner: boolean): SegmentData => {
    const segmentColor = ifEmptySegments ? getEmptySegmentColor(isInner) : getSegmentColor(segment, isInner)

    return {
      value: data[segment].value,
      originalValue: data[segment].originalValue,
      itemStyle: {
        ...data[segment].itemStyle,
        color: segmentColor,
        borderColor: '#fff',
        borderWidth: isInner ? 1 : 2,
      },
    }
  }

  return { createSegment }
}
